import { toPng } from "html-to-image";

export const captureNodeAsPng = async (node) => {
  if (!node) throw new Error("Nothing to capture");
  return await toPng(node, {
    cacheBust: true,
    pixelRatio: 2,
    useCORS: true,
    skipFonts: true,
  });
};

export const findSvgInSource = (source) => {
  const node = source?.current ?? source;
  if (!node) return null;
  if (typeof node.querySelector === "function") {
    return node.querySelector("svg");
  }
  if (node?.tagName?.toLowerCase?.() === "svg") {
    return node;
  }
  return null;
};

export const svgToPngDataUrl = (svgNode, targetWidth) => {
  return new Promise((resolve, reject) => {
    if (!svgNode) {
      reject(new Error("Missing SVG element"));
      return;
    }

    const clonedSvg = svgNode.cloneNode(true);
    const viewBox = clonedSvg.getAttribute("viewBox");
    const [, , vbWidth, vbHeight] = viewBox
      ? viewBox.split(" ").map(Number)
      : [];

    const naturalWidth =
      vbWidth || svgNode.getBoundingClientRect().width || targetWidth;
    const naturalHeight =
      vbHeight || svgNode.getBoundingClientRect().height || targetWidth * 0.6;
    const width = targetWidth;
    const height = naturalWidth
      ? (naturalHeight * width) / naturalWidth
      : targetWidth * 0.6;

    const svgString = new XMLSerializer().serializeToString(clonedSvg);
    const blob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve({ dataUrl: canvas.toDataURL("image/png"), width, height });
    };

    img.onerror = (err) => reject(err);
    img.src = url;
  });
};

export const fetchPngDataUrl = async (path) => {
  const res = await fetch(path, { cache: "no-cache" });
  if (!res.ok) throw new Error(`Failed to load ${path}`);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const getLogoDataUrl = async () => {
  try {
    return await fetchPngDataUrl("/img/ac_black_logo.png");
  } catch (e) {
    console.warn("Primary logo load failed, falling back to default", e);
    return await fetchPngDataUrl("/img/logo.png");
  }
};

export const placeCornerLogo = (doc, logoDataUrl, options = {}) => {
  if (!logoDataUrl) return;
  const pageWidth = doc.internal.pageSize.getWidth();
  const width = options.width ?? 110;
  const top = options.top ?? 18;
  const fmt = logoDataUrl.startsWith("data:image/svg") ? "SVG" : "PNG";
  const props = doc.getImageProperties(logoDataUrl);
  const height = (props.height * width) / props.width;
  doc.addImage(logoDataUrl, fmt, pageWidth - width - 24, top, width, height);
};

export const placeCenteredLogo = (doc, logoDataUrl, startY = 84, width = 210) => {
  if (!logoDataUrl) return startY;
  const pageWidth = doc.internal.pageSize.getWidth();
  const props = doc.getImageProperties(logoDataUrl);
  const logoHeight = (props.height * width) / props.width;
  const fmt = logoDataUrl.startsWith("data:image/svg") ? "SVG" : "PNG";
  doc.addImage(
    logoDataUrl,
    fmt,
    (pageWidth - width) / 2,
    startY,
    width,
    logoHeight,
  );
  return startY + logoHeight;
};

export const fitBox = (sourceWidth, sourceHeight, maxWidth, maxHeight) => {
  if (!sourceWidth || !sourceHeight) {
    return { width: maxWidth, height: maxHeight };
  }

  const ratio = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
  return {
    width: sourceWidth * ratio,
    height: sourceHeight * ratio,
  };
};

export const chunkItems = (items, size) => {
  const chunks = [];

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }

  return chunks;
};

export const colorToRgb = (color) => {
  if (Array.isArray(color)) return color;
  const normalized = String(color ?? "").trim();
  if (!normalized.startsWith("#")) return [74, 144, 217];
  const hex = normalized.slice(1);
  const fullHex =
    hex.length === 3
      ? hex
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : hex;
  return [
    parseInt(fullHex.slice(0, 2), 16),
    parseInt(fullHex.slice(2, 4), 16),
    parseInt(fullHex.slice(4, 6), 16),
  ];
};

export const drawPdfSummaryRows = (doc, rows, { x, y, width }) => {
  const visibleRows = (rows ?? []).filter((row) => row && row.show !== false);
  if (!visibleRows.length) return;

  const topY = y + 8;
  const gap = 6;
  let cursorX = x + width - 14;

  [...visibleRows].reverse().forEach((row) => {
    const label = `${row.label}: ${row.value}`;
    const badgeWidth = doc.getTextWidth(label) + 28;
    const badgeX = cursorX - badgeWidth;
    const [r, g, b] = colorToRgb(row.color);

    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(222, 222, 222);
    doc.roundedRect(badgeX, topY, badgeWidth, 18, 9, 9, "FD");
    doc.setFillColor(r, g, b);
    doc.circle(badgeX + 10, topY + 9, 3.2, "F");
    doc.setFontSize(9);
    doc.setTextColor(65, 65, 65);
    doc.text(label, badgeX + 18, topY + 12);

    cursorX = badgeX - gap;
  });
};

export const drawChartCard = (
  doc,
  {
    title,
    dataUrl,
    summaryRows = [],
    x,
    y,
    width,
    height,
    accentColor,
    borderColor,
    fillColor,
  },
) => {
  const titleSpace = summaryRows.length ? 44 : 28;
  const horizontalPadding = 16;
  const verticalPadding = 10;

  doc.setFillColor(...fillColor);
  doc.setDrawColor(...borderColor);
  doc.setLineWidth(1);
  doc.roundedRect(x, y, width, height, 12, 12, "FD");

  doc.setFontSize(12);
  doc.setTextColor(...accentColor);
  doc.text(title, x + horizontalPadding, y + 20);
  drawPdfSummaryRows(doc, summaryRows, { x, y, width });

  const props = doc.getImageProperties(dataUrl);
  const fitted = fitBox(
    props.width,
    props.height,
    width - horizontalPadding * 2,
    height - titleSpace - verticalPadding * 2,
  );
  const imageX = x + (width - fitted.width) / 2;
  const imageY =
    y +
    titleSpace +
    verticalPadding +
    (height - titleSpace - verticalPadding * 2 - fitted.height) / 2;
  doc.addImage(dataUrl, "PNG", imageX, imageY, fitted.width, fitted.height);
};

export const drawPageChrome = (doc, logoDataUrl, pageNumber, totalPages, lang) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerLabel = lang === "dk" ? "Side" : "Page";

  placeCornerLogo(doc, logoDataUrl);
  doc.setDrawColor(235, 196, 196);
  doc.setLineWidth(0.8);
  doc.line(42, 64, pageWidth - 42, 64);
  doc.line(42, pageHeight - 32, pageWidth - 42, pageHeight - 32);
  doc.setFontSize(9);
  doc.setTextColor(133, 88, 88);
  doc.text(
    `${footerLabel} ${pageNumber} / ${totalPages}`,
    pageWidth / 2,
    pageHeight - 18,
    {
      align: "center",
    },
  );
};
